import React from "react";
import { AlertCircle, Sparkles } from "lucide-react";
import { Card, CardContent, StatusBadge } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { AttributeReviewContextHeaderProps } from "./AttributeReviewContextHeader.types";

export const AttributeReviewContextHeader: React.FC<
  AttributeReviewContextHeaderProps
> = ({
  context,
  needsReview,
  lowConfidence,
  activeSummaryFilter,
  onSummaryFilterChange,
}) => {
  return (
    <Card className="mb-6">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              SKU ID
            </p>
            <p className="text-sm font-mono font-medium text-slate-900 dark:text-slate-100">
              {context.skuId}
            </p>
          </div>
          <div className="w-px h-10 bg-slate-200 dark:bg-slate-700" />
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Mapping Run
            </p>
            <p className="text-sm font-mono font-medium text-slate-900 dark:text-slate-100">
              {context.mappingRunId}
            </p>
          </div>
          <div className="w-px h-10 bg-slate-200 dark:bg-slate-700" />
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Model Version
            </p>
            <StatusBadge status="ACTIVE" showDot={false} className="mt-1">
              <Sparkles className="w-3 h-3 mr-1" />
              {context.modelVersion}
            </StatusBadge>
          </div>
          <div className="w-px h-10 bg-slate-200 dark:bg-slate-700" />
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Mapping
            </p>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {context.sourceMarketplace} → {context.targetMarketplace}
            </p>
          </div>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => onSummaryFilterChange("NEEDS_REVIEW")}
            className={cn(
              "inline-flex items-center rounded-full transition-all",
              "focus:outline-none focus:ring-2 focus:ring-amber-500/30",
              activeSummaryFilter === "NEEDS_REVIEW" &&
                "ring-2 ring-amber-500/30",
            )}
            aria-pressed={activeSummaryFilter === "NEEDS_REVIEW"}
            data-testid="filter-needs-review"
          >
            <StatusBadge status="WARNING" className="text-sm cursor-pointer">
              <AlertCircle className="w-3.5 h-3.5 mr-1" />
              {needsReview} To review
            </StatusBadge>
          </button>
          {lowConfidence > 0 && (
            <button
              type="button"
              onClick={() => onSummaryFilterChange("LOW_CONFIDENCE")}
              className={cn(
                "inline-flex items-center rounded-full transition-all",
                "focus:outline-none focus:ring-2 focus:ring-rose-500/30",
                activeSummaryFilter === "LOW_CONFIDENCE" &&
                  "ring-2 ring-rose-500/30",
              )}
              aria-pressed={activeSummaryFilter === "LOW_CONFIDENCE"}
              data-testid="filter-low-confidence"
            >
              <StatusBadge status="FAILED" className="text-sm cursor-pointer">
                <AlertCircle className="w-3.5 h-3.5 mr-1" />
                {lowConfidence} Low Confidence
              </StatusBadge>
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
